import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SunIcon, Clock3Icon, MoonIcon, Check, Loader2, RefreshCw } from 'lucide-react';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useSettings, TimePreference } from '@/hooks/useSettings';
import { SettingsApi } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import debounce from 'lodash.debounce';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Component starts directly here
const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const [darkMode, setDarkMode] = useDarkMode();
  const { settings, updatePreferences, updateAI, updatePosts, fetchSettingsFromBackend } = useSettings();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('preferences');
  const [isSyncing, setIsSyncing] = useState(false);

  // Fetch the latest settings when the modal is opened
  useEffect(() => {
    if (isOpen) {
      fetchSettingsFromBackend();
    }
  }, [isOpen, fetchSettingsFromBackend]);

  // Local state for editing
  const [localPreferences, setLocalPreferences] = useState({
    darkMode,
    optimalTimes: [...settings.preferences.optimalTimes]
  });

  const [localAISettings, setLocalAISettings] = useState({
    systemPrompt: settings.ai.systemPrompt,
    temperature: settings.ai.temperature,
    shouldUseWebSearch: settings.ai.shouldUseWebSearch
  });

  const [localPostSettings, setLocalPostSettings] = useState({
    autoArchiveDays: settings.posts.autoArchiveDays,
    defaultPlatform: settings.posts.defaultPlatform
  });

  // Update local state when settings change
  useEffect(() => {
    setLocalPreferences({
      darkMode,
      optimalTimes: [...settings.preferences.optimalTimes]
    });
    
    setLocalAISettings({
      systemPrompt: settings.ai.systemPrompt,
      temperature: settings.ai.temperature,
      shouldUseWebSearch: settings.ai.shouldUseWebSearch
    });
    
    setLocalPostSettings({
      autoArchiveDays: settings.posts.autoArchiveDays,
      defaultPlatform: settings.posts.defaultPlatform
    });
  }, [settings, darkMode]);

  // Auto-save AI settings to backend when they change
  // Use debounce to avoid excessive API calls
  const debouncedSaveAISettings = useMemo(
    () => debounce(async (newSettings: typeof localAISettings) => {
      // Skip initial render and only save if values differ from settings
      // We're only auto-saving temperature and web search settings, not system prompt
      if (
        newSettings.temperature === settings.ai.temperature &&
        newSettings.shouldUseWebSearch === settings.ai.shouldUseWebSearch
      ) {
        return;
      }

      try {
        setIsSyncing(true);
        await SettingsApi.syncAISettings({
          temperature: newSettings.temperature,
          webSearchEnabled: newSettings.shouldUseWebSearch
        });
        
        // Update local settings (excluding systemPrompt)
        updateAI({
          temperature: newSettings.temperature,
          shouldUseWebSearch: newSettings.shouldUseWebSearch
        });
        
        // No toast notification for automatic saves
      } catch (error) {
        console.error('Failed to sync AI settings:', error);
        toast({
          title: 'Error',
          description: 'Failed to save AI settings. Please try again.',
          type: 'error'
        });
      } finally {
        setIsSyncing(false);
      }
    }, 1000),
    [settings.ai, toast, updateAI]
  );

  // Auto-save AI settings when they change
  useEffect(() => {
    debouncedSaveAISettings(localAISettings);
    
    // Cleanup on unmount
    return () => {
      debouncedSaveAISettings.cancel();
    };
  }, [localAISettings, debouncedSaveAISettings]);

  // Handle time setting changes
  const handleTimeChange = (index: number, newValue: TimePreference) => {
    const updatedTimes = [...localPreferences.optimalTimes];
    updatedTimes[index] = { ...newValue };
    setLocalPreferences(prev => ({
      ...prev,
      optimalTimes: updatedTimes
    }));
    
    // Update local settings
    updatePreferences({
      ...localPreferences,
      optimalTimes: updatedTimes
    });
    
    // Sync with backend
    syncPreferencesWithBackend({
      optimalTimes: updatedTimes
    });
  };
  
  // Sync preferences with backend
  const syncPreferencesWithBackend = async (preferences: Partial<typeof localPreferences>) => {
    try {
      // Only sync optimal posting times for now
      if (preferences.optimalTimes) {
        await SettingsApi.updateSystemSettings({
          optimalPostingTimes: preferences.optimalTimes
        });
      }
    } catch (error) {
      console.error('Failed to sync preferences with backend:', error);
      toast({
        title: 'Error',
        description: 'Failed to save preferences to server. Your settings are saved locally but won\'t sync across devices.',
        type: 'error'
      });
    }
  };
  
  // Handle dark mode change
  const handleDarkModeChange = (checked: boolean) => {
    const newPrefs = {
      ...localPreferences,
      darkMode: checked
    };
    setLocalPreferences(newPrefs);
    setDarkMode(checked);
    updatePreferences(newPrefs);
  };

  // Handle AI settings change
  const handleAIChange = (key: keyof typeof localAISettings, value: any) => {
    setLocalAISettings(prev => ({
      ...prev,
      [key]: value
    }));
    // Don't auto-save systemPrompt - it will be saved with the save button
    if (key !== 'systemPrompt') {
      // Auto-save is handled by the useEffect
    }
  };
  
  // Handle post settings change
  const handlePostSettingsChange = (key: keyof typeof localPostSettings, value: any) => {
    const newSettings = {
      ...localPostSettings,
      [key]: value
    };
    setLocalPostSettings(newSettings);
    updatePosts(newSettings);
  };

  // Reset preference settings ONLY
  const resetPreferenceSettings = async () => {
    const defaultPreferences = {
      darkMode: false,
      optimalTimes: [
        { name: "Morning", hour: 9, minute: 0 },
        { name: "Noon", hour: 12, minute: 0 },
        { name: "Evening", hour: 18, minute: 0 }
      ]
    };
    
    // Only update preferences, not other settings
    setLocalPreferences(defaultPreferences);
    updatePreferences(defaultPreferences);
    setDarkMode(false);
    
    // Sync with backend
    try {
      await SettingsApi.updateSystemSettings({
        optimalPostingTimes: defaultPreferences.optimalTimes
      });
      
      toast({
        title: 'Preferences Reset',
        description: 'Appearance preferences have been reset to defaults.',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to reset preferences on server:', error);
      toast({
        title: 'Partial Reset',
        description: 'Preferences reset locally but failed to sync with server.',
        type: 'warning'
      });
    }
  };
  
  // Reset AI settings ONLY
  const resetAISettings = async () => {
    try {
      setIsSyncing(true);
      
      // Reset on server
      const defaultSettings = await SettingsApi.resetAISettings();
      
      // Update local state with proper type safety
      const newAISettings = {
        systemPrompt: defaultSettings.aiSystemPrompt || "",
        temperature: defaultSettings.temperature ?? 1.0,
        shouldUseWebSearch: defaultSettings.webSearchEnabled ?? true
      };
      
      // Only update AI settings, not other settings
      setLocalAISettings(newAISettings);
      updateAI(newAISettings);
      
      toast({
        title: 'AI Settings Reset',
        description: 'AI settings have been reset to defaults.',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to reset AI settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to reset settings.',
        type: 'error'
      });
    } finally {
      setIsSyncing(false);
    }
  };
  
  // Reset post management settings ONLY
  const resetPostSettings = async () => {
    const defaultPosts = {
      autoArchiveDays: 30,
      defaultPlatform: 'x'
    };
    
    // Only update post settings, not other settings
    setLocalPostSettings(defaultPosts);
    updatePosts(defaultPosts);
    
    // Sync with backend
    try {
      await SettingsApi.updateSystemSettings({
        autoArchiveDays: defaultPosts.autoArchiveDays,
        defaultPlatform: defaultPosts.defaultPlatform
      });
      
      toast({
        title: 'Post Settings Reset',
        description: 'Post management settings have been reset to defaults.',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to reset post settings on server:', error);
      toast({
        title: 'Partial Reset',
        description: 'Post settings reset locally but failed to sync with server.',
        type: 'warning'
      });
    }
  };

  // Save system prompt explicitly
  const saveSystemPrompt = async () => {
    try {
      setIsSyncing(true);
      await SettingsApi.syncAISettings({
        aiSystemPrompt: localAISettings.systemPrompt,
      });
      
      // Update local settings
      updateAI({
        systemPrompt: localAISettings.systemPrompt
      });
      
      toast({
        title: 'System Prompt Saved',
        description: 'Your system prompt has been saved.',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to save system prompt:', error);
      toast({
        title: 'Error',
        description: 'Failed to save system prompt. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSyncing(false);
    }
  };
  
  // Save post settings explicitly
  const savePostSettings = async () => {
    try {
      // Sanitize auto-archive days
      const autoArchiveDays = Math.max(1, Math.min(365, parseInt(localPostSettings.autoArchiveDays.toString()) || 30));
      
      const newSettings = {
        ...localPostSettings,
        autoArchiveDays
      };
      
      setLocalPostSettings(newSettings);
      updatePosts(newSettings);
      
      // Sync with backend
      await SettingsApi.updateSystemSettings({
        autoArchiveDays: autoArchiveDays,
        defaultPlatform: newSettings.defaultPlatform
      });
      
      toast({
        title: 'Post Settings Saved',
        description: 'Your post management settings have been saved.',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to save post settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save post settings. Please try again.',
        type: 'error'
      });
    }
  };

  // Enhanced time input component
  const TimeInputEnhanced: React.FC<{
    value: TimePreference;
    onChange: (value: TimePreference) => void;
  }> = ({ value, onChange }) => {
    // Convert 24-hour format to 12-hour format with AM/PM
    const convert24to12 = (hour24: number): { hour12: number, ampm: 'am' | 'pm' } => {
      if (hour24 === 0) return { hour12: 12, ampm: 'am' };
      if (hour24 === 12) return { hour12: 12, ampm: 'pm' };
      if (hour24 > 12) return { hour12: hour24 - 12, ampm: 'pm' };
      return { hour12: hour24, ampm: 'am' };
    };

    // Convert 12-hour format back to 24-hour format
    const convert12to24 = (hour12: number, ampm: 'am' | 'pm'): number => {
      if (hour12 === 12) {
        return ampm === 'am' ? 0 : 12;
      }
      return ampm === 'am' ? hour12 : hour12 + 12;
    };

    // Get 12-hour format values
    const { hour12, ampm } = convert24to12(value.hour);
    
    // Generate hours (1-12) and minutes (0-55 in 5 min increments) for select options
    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5);
    
    const handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newHour12 = parseInt(e.target.value);
      // Convert back to 24-hour for storage
      const newHour24 = convert12to24(newHour12, ampm);
      onChange({ ...value, hour: newHour24 });
    };
    
    const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newMinute = parseInt(e.target.value);
      onChange({ ...value, minute: newMinute });
    };
    
    const handleAmPmChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newAmPm = e.target.value as 'am' | 'pm';
      // Convert current 12-hour to 24-hour with new AM/PM
      const newHour24 = convert12to24(hour12, newAmPm);
      onChange({ ...value, hour: newHour24 });
    };

    return (
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          {value.name === "Morning" ? (
            <SunIcon className="h-5 w-5 text-amber-500" />
          ) : value.name === "Noon" ? (
            <Clock3Icon className="h-5 w-5 text-blue-500" />
          ) : (
            <MoonIcon className="h-5 w-5 text-indigo-500" />
          )}
        </div>
        <Label className="flex-grow">{value.name}</Label>
        <div className="flex items-center gap-2">
          <select
            value={hour12}
            onChange={handleHourChange}
            className="w-16 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {hours.map(hour => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>
          <span>:</span>
          <select
            value={value.minute}
            onChange={handleMinuteChange}
            className="w-16 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {minutes.map(minute => (
              <option key={minute} value={minute}>
                {minute.toString().padStart(2, '0')}
              </option>
            ))}
          </select>
          <select
            value={ampm}
            onChange={handleAmPmChange}
            className="w-20 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="am">AM</option>
            <option value="pm">PM</option>
          </select>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="preferences" className="w-full" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3">
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="ai">AI Settings</TabsTrigger>
            <TabsTrigger value="post-management">Post Management</TabsTrigger>
          </TabsList>
          
          {/* Preferences Tab */}
          <TabsContent value="preferences" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Appearance</CardTitle>
                  <CardDescription>Customize how the application looks</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={resetPreferenceSettings}
                  className="flex items-center gap-1"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reset
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="dark-mode">Dark Mode</Label>
                  <div className="flex items-center space-x-2">
                    <SunIcon className="h-4 w-4 text-amber-500" />
                    <Switch 
                      id="dark-mode"
                      checked={localPreferences.darkMode}
                      onCheckedChange={handleDarkModeChange}
                    />
                    <MoonIcon className="h-4 w-4 text-indigo-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Optimal Posting Times</CardTitle>
                  <CardDescription>Configure default posting times for scheduling</CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {localPreferences.optimalTimes.map((time, index) => (
                  <TimeInputEnhanced
                    key={time.name}
                    value={time}
                    onChange={(newValue) => handleTimeChange(index, newValue)}
                  />
                ))}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* AI Settings Tab */}
          <TabsContent value="ai" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>AI Behavior</CardTitle>
                  <CardDescription>Configure how the AI generates content</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {isSyncing ? (
                    <div className="text-sm text-muted-foreground flex items-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground flex items-center">
                      <Check className="mr-2 h-4 w-4 text-green-500" />
                      Auto-saved
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={resetAISettings}
                    disabled={isSyncing}
                    className="flex items-center gap-1"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reset
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="system-prompt">System Prompt</Label>
                  <Textarea
                    id="system-prompt"
                    value={localAISettings.systemPrompt}
                    onChange={(e) => handleAIChange('systemPrompt', e.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-muted-foreground">
                      This prompt guides the AI's behavior when generating content.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={saveSystemPrompt}
                      disabled={isSyncing || localAISettings.systemPrompt === settings.ai.systemPrompt}
                      className="flex items-center gap-1"
                    >
                      {isSyncing ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="temperature">Temperature: {localAISettings.temperature.toFixed(1)}</Label>
                  </div>
                  <Input
                    id="temperature"
                    type="range"
                    min={0}
                    max={2}
                    step={0.1}
                    value={localAISettings.temperature}
                    onChange={(e) => handleAIChange('temperature', parseFloat(e.target.value))}
                    className="cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>More Precise (0.0)</span>
                    <span>Balanced (1.0)</span>
                    <span>More Creative (2.0)</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-2">
                  <div>
                    <Label htmlFor="web-search">Enable Web Search</Label>
                    <p className="text-xs text-muted-foreground">
                      Allow AI to search the web for current information when generating content
                    </p>
                  </div>
                  <Switch 
                    id="web-search" 
                    checked={localAISettings.shouldUseWebSearch}
                    onCheckedChange={(checked) => handleAIChange('shouldUseWebSearch', checked)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Post Management Tab */}
          <TabsContent value="post-management" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Post Management</CardTitle>
                  <CardDescription>Configure how your posts are handled</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={resetPostSettings}
                  className="flex items-center gap-1"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reset
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="auto-archive">Auto-Archive Days</Label>
                  <Input
                    id="auto-archive"
                    type="number"
                    min={1}
                    max={365}
                    value={localPostSettings.autoArchiveDays}
                    onChange={(e) => handlePostSettingsChange('autoArchiveDays', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Number of days after which posts will be automatically archived
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="default-platform">Default Platform</Label>
                  <select
                    id="default-platform"
                    value={localPostSettings.defaultPlatform}
                    onChange={(e) => handlePostSettingsChange('defaultPlatform', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="x">X (formerly Twitter)</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="facebook">Facebook</option>
                    <option value="instagram">Instagram</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    The default platform selected when creating new posts
                  </p>
                </div>
              </CardContent>
            </Card>
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                variant="outline" 
                onClick={savePostSettings}
                className="flex items-center gap-1"
              >
                <Check className="h-4 w-4" />
                Save Changes
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;